'use client';

import { useState, type CSSProperties, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionShell } from '../_shared/SectionShell';

/**
 * "Was this article helpful?" feedback form for a help-center
 * article. Renders a yes/no pair and an optional comment textarea.
 * Submits to
 * {@code POST /v1/public/{tenantId}/help-center/article/{articleId}/feedback}
 * — the portal renderer injects {@code tenantId} and {@code articleId}
 * server-side via the route resolver so anonymous callers don't have
 * to know either.
 *
 * <p>The block is intentionally minimal: no client-side adapter, no
 * runtime SourceContext. Help-center feedback is a write-only path —
 * there's no list/render surface the runtime would otherwise own — so
 * coupling it to a one-off Source would be more ceremony than it's
 * worth. When other write-only blocks land we can extract a shared
 * "AnonymousSubmitForm" primitive.
 */
export interface HelpArticleFeedbackFormProps {
  /**
   * Server-injected tenant id. The portal render endpoint
   * substitutes the active tenant; leaving it undefined falls back to
   * "the current window's portal path", which works in the canonical
   * deployment but not in CDN-fronted preview shells.
   */
  tenantId?: number | string;
  /**
   * Server-injected article id from the route resolver. Required —
   * without it the block can't post.
   */
  articleId?: number | string;
  /** Section heading shown above the yes/no pair. */
  title?: string;
  /** Section subhead. Optional. */
  subtitle?: string;
  /** Label for the affirmative vote. */
  yesLabel?: string;
  /** Label for the negative vote. */
  noLabel?: string;
  /** When true, the form exposes a comment textarea after a vote. */
  acceptComment?: boolean;
  /** Placeholder for the optional comment textarea. */
  commentPlaceholder?: string;
  /** Submit button label (only shown when a comment is collected). */
  submitLabel?: string;
  /** Message rendered after a successful submission. */
  thankYouMessage?: string;
  /** Override the API base URL. Defaults to the window origin. */
  apiBaseUrl?: string;
}

type Vote = 'yes' | 'no';

type FormState =
  | { kind: 'idle' }
  | { kind: 'voted'; vote: Vote }
  | { kind: 'submitting' }
  | { kind: 'submitted' }
  | { kind: 'error'; message: string };

function resolveBase(apiBaseUrl: string | undefined): string {
  if (apiBaseUrl && apiBaseUrl.trim()) return apiBaseUrl.replace(/\/$/, '');
  if (typeof window !== 'undefined' && window.location) return window.location.origin;
  return '';
}

export function HelpArticleFeedbackForm({
  tenantId,
  articleId,
  title,
  subtitle,
  yesLabel,
  noLabel,
  acceptComment = true,
  commentPlaceholder,
  submitLabel,
  thankYouMessage,
  apiBaseUrl,
}: HelpArticleFeedbackFormProps) {
  const { t } = useTranslation('site-builder-blocks');
  const titleText = title ?? t('helpCenter.feedback.title');
  const yesText = yesLabel ?? t('helpCenter.feedback.yes');
  const noText = noLabel ?? t('helpCenter.feedback.no');
  const commentPlaceholderText = commentPlaceholder ?? t('helpCenter.feedback.commentPlaceholder');
  const submitText = submitLabel ?? t('helpCenter.feedback.submit');
  const thankYouText = thankYouMessage ?? t('helpCenter.feedback.thanks');
  const [state, setState] = useState<FormState>({ kind: 'idle' });
  const [comment, setComment] = useState('');

  if (articleId == null || articleId === '') {
    // No article context — block is misconfigured. Render nothing
    // rather than a broken form (the route resolver should always
    // inject it; missing means a static preview page that doesn't
    // bind to an article).
    return null;
  }

  async function post(vote: Vote, includeComment: boolean): Promise<void> {
    const helpful = vote === 'yes';
    const base = resolveBase(apiBaseUrl);
    const tenantSegment = tenantId == null || tenantId === '' ? '' : `${tenantId}`;
    if (!tenantSegment) {
      setState({ kind: 'error', message: t('helpCenter.feedback.errorNoScope') });
      return;
    }
    const url = `${base}/v1/public/${encodeURIComponent(tenantSegment)}/help-center/article/${encodeURIComponent(`${articleId}`)}/feedback`;
    const body: Record<string, unknown> = { helpful };
    if (includeComment && comment.trim()) body.comment = comment.trim();
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'include',
      });
      if (!res.ok) {
        setState({
          kind: 'error',
          message: t('helpCenter.feedback.errorHttp', { status: res.status }),
        });
        return;
      }
      setState({ kind: 'submitted' });
    } catch (e) {
      setState({
        kind: 'error',
        message: e instanceof Error ? e.message : t('helpCenter.feedback.errorNetwork'),
      });
    }
  }

  function onVote(vote: Vote): void {
    if (!acceptComment) {
      setState({ kind: 'submitting' });
      void post(vote, false).then(() => {
        // post() owns the terminal state.
      });
      return;
    }
    setState({ kind: 'voted', vote });
  }

  async function onSubmitComment(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (state.kind !== 'voted') return;
    setState({ kind: 'submitting' });
    await post(state.vote, true);
  }

  if (state.kind === 'submitted') {
    return (
      <SectionShell title={titleText} subtitle={subtitle}>
        <div
          role="status"
          aria-live="polite"
          style={{
            padding: 'var(--space-4, 16px) var(--space-5, 20px)',
            background: 'var(--color-bg-soft, #f5f5f5)',
            borderRadius: 8,
            color: 'var(--color-text, #1a1a1a)',
            fontSize: 'var(--font-size-base, 15px)',
          }}
        >
          {thankYouText}
        </div>
      </SectionShell>
    );
  }

  return (
    <SectionShell title={titleText} subtitle={subtitle}>
      <div
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          aria-pressed={state.kind === 'voted' && state.vote === 'yes'}
          disabled={state.kind === 'submitting'}
          onClick={() => onVote('yes')}
          style={voteButtonStyle(state.kind === 'voted' && state.vote === 'yes')}
        >
          <span aria-hidden style={{ marginRight: 6 }}>
            👍
          </span>
          {yesText}
        </button>
        <button
          type="button"
          aria-pressed={state.kind === 'voted' && state.vote === 'no'}
          disabled={state.kind === 'submitting'}
          onClick={() => onVote('no')}
          style={voteButtonStyle(state.kind === 'voted' && state.vote === 'no')}
        >
          <span aria-hidden style={{ marginRight: 6 }}>
            👎
          </span>
          {noText}
        </button>
      </div>

      {state.kind === 'voted' && acceptComment ? (
        <form
          onSubmit={(e) => {
            void onSubmitComment(e);
          }}
          style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          <label
            htmlFor="help-article-feedback-comment"
            style={{ fontSize: '13px', color: 'var(--color-text-secondary, #555)' }}
          >
            {commentPlaceholderText}
          </label>
          <textarea
            id="help-article-feedback-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            maxLength={2000}
            style={{
              padding: 'var(--space-2-5, 10px) var(--space-3, 12px)',
              borderRadius: 8,
              border: '1px solid var(--color-border, #d8d8d8)',
              fontFamily: 'var(--font-family-body, inherit)',
              fontSize: 'var(--font-size-sm, 14px)',
              resize: 'vertical',
              background: 'var(--color-bg, #fff)',
              color: 'var(--color-text, #1a1a1a)',
            }}
          />
          <div>
            <button type="submit" style={primaryButtonStyle()}>
              {submitText}
            </button>
          </div>
        </form>
      ) : null}

      {state.kind === 'error' ? (
        <div
          role="alert"
          style={{
            marginTop: 12,
            padding: 'var(--space-2-5, 10px) var(--space-3-5, 14px)',
            background: 'var(--color-bg-danger, #fdecea)',
            color: 'var(--color-text-danger, #b3261e)',
            borderRadius: 6,
            fontSize: 'var(--font-size-sm, 14px)',
          }}
        >
          {state.message}
        </div>
      ) : null}
    </SectionShell>
  );
}

function voteButtonStyle(active: boolean): CSSProperties {
  return {
    padding: 'var(--space-2, 8px) var(--space-4, 16px)',
    borderRadius: 999,
    border: `1px solid ${active ? 'var(--color-accent, #2156c4)' : 'var(--color-border, #d8d8d8)'}`,
    background: active ? 'var(--color-accent-soft, #e4ecfa)' : 'var(--color-bg, #fff)',
    color: 'var(--color-text, #1a1a1a)',
    fontSize: 'var(--font-size-sm, 14px)',
    cursor: 'pointer',
  };
}

function primaryButtonStyle(): CSSProperties {
  return {
    padding: 'var(--space-2, 8px) var(--space-4, 16px)',
    borderRadius: 6,
    border: 'none',
    background: 'var(--color-accent, #2156c4)',
    color: 'var(--color-on-accent, #fff)',
    fontSize: 'var(--font-size-sm, 14px)',
    cursor: 'pointer',
  };
}
